import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import { log } from "../../logger";

/**
 * The one create-catalog dialog (catalog-selector 02). The Catalogs page owns
 * it; `createEntry` selects the new catalog and reloads the lists, so the page
 * stays put and simply renders the new catalog as current.
 */
export function CreateCatalogDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const createEntry = useAppStore((s) => s.createEntry);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  // A fresh open never inherits the abandoned name of the previous attempt.
  useEffect(() => {
    if (open) setName("");
  }, [open]);

  const close = () => {
    if (busy) return;
    setName("");
    onClose();
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await createEntry(trimmed);
      setName("");
      onClose();
    } catch (err) {
      // Keep the dialog open so the typed name is not lost; the API message is
      // not a user-facing string here (no catalog-specific error key yet).
      log.warn({ err }, "[catalog] Create catalog failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs" data-testid="create-entry-dialog">
      <DialogTitle>{t("createEntryTitle")}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label={t("entryName")}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void submit();
          }}
          fullWidth
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          {t("cancel")}
        </Button>
        <Button variant="contained" disabled={!name.trim() || busy} onClick={() => void submit()}>
          {t("create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
